import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI
import Box from '@mui/material/Box';
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";

// MUI Icons
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import CloseIcon from "@mui/icons-material/Close";

// Custom Components
import dataController from "@/lib/dataController";
import model from "@/models/user";
import AppPage from "@/ui/AppPage/AppPage";
import GridContainer from "@/ui/Grid/GridContainer";
import GridItem from "@/ui/Grid/GridItem";
import TextControl from "@/controls/TextControl";
import FormButton from "@/ui/Form/FormButton";
import PasswordChangeDialog from "@/views/ProfilePage/PasswordChangeDialog";

import DialogContext from "@/ui/DialogContext/DialogContext";
import InfoBox from '@/ui/InfoBox';
import UserContext from "@/components/UserContext/UserContext";

// Types
import { IFieldText } from "@/@types/models/model";

function ProfilePage() {
  const userContext = useContext(UserContext);
  const dialogContext = useContext(DialogContext);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dc = new dataController(model);

  const handlePasswordChange = () => {
    dialogContext.showDialog(PasswordChangeDialog, {
      dc: dc,
      form: "password_change"
    });
  };

  const backHome = () => {
    navigate(userContext?.getDefaultUrl(userContext.organisationId) || '/');
  };

  const noChange = () => { }

  const hideHelperText = (field: IFieldText) => {
    return {
      ...field,
      hideHelperText: true
    } as IFieldText;
  }

  const fieldOrganisationName: IFieldText = {
    title: "Organisation",
    source: "owner_organisation_name",
    ttoken: "users.owner_organisation_id",
    type: "text",
    hideHelperText: true
  }

  return (
    <AppPage showBackgroudPhoto>
      <Box m={2}>
        <Container maxWidth="sm">
          <Card>
            <CardHeader
              title={t("titles.user_profile")}
              action={
                <IconButton onClick={backHome}>
                  <CloseIcon />
                </IconButton>
              }
            />
            <CardContent>


              <GridContainer>
                <GridItem size={12}>
                  <TextControl
                    formMode="form"
                    controlMode="view"
                    value={userContext?.email ? userContext.email : ""}
                    field={hideHelperText(dc.getField("email") as IFieldText)}
                    onChange={noChange}
                  />
                </GridItem>
                <GridItem size={{xs: 12, sm: 6}}>
                  <TextControl
                    formMode="form"
                    controlMode="view"
                    value={userContext?.firstName || ""}
                    field={hideHelperText(dc.getField("first_name") as IFieldText)}
                    onChange={noChange}
                  />
                </GridItem>
                <GridItem size={{xs: 12, sm: 6}}>
                  <TextControl
                    formMode="form"
                    controlMode="view"
                    value={userContext?.lastName || ""}
                    field={hideHelperText(dc.getField("last_name") as IFieldText)}
                    onChange={noChange}
                  />
                </GridItem>
                <GridItem size={{xs: 12, sm: 6}}>
                  <TextControl
                    formMode="form"
                    controlMode="view"
                    value={userContext?.oib || ""}
                    field={hideHelperText(dc.getField("oib") as IFieldText)}
                    onChange={noChange}
                  />
                </GridItem>
                <GridItem size={{xs: 12, sm: 6}}>
                  <TextControl
                    formMode="form"
                    controlMode="view"
                    value={userContext?.mobitel || ""}
                    field={hideHelperText(dc.getField("mobitel") as IFieldText)}
                    onChange={noChange}
                  />
                </GridItem>
                {userContext?.organisationId ?
                  <GridItem size={12}>
                    <TextControl
                      formMode="form"
                      controlMode="view"
                      value={userContext.organisationName}
                      field={fieldOrganisationName}
                      onChange={noChange}
                    />
                  </GridItem>
                  : null
                }
                {userContext?.planUdrugaName ?
                  <GridItem size={12}>
                    <TextControl
                      formMode="form"
                      controlMode="view"
                      value={userContext.planUdrugaName}
                      field={hideHelperText(dc.getField("hps_plan_udruga_naziv") as IFieldText)}
                      onChange={noChange}
                    />
                  </GridItem>
                  : null
                }
                {userContext?.spvName ?
                  <GridItem size={12}>
                    <TextControl
                      formMode="form"
                      controlMode="view"
                      value={userContext.spvName}
                      field={hideHelperText(dc.getField("hps_spv_naziv") as IFieldText)}
                      onChange={noChange}
                    />
                  </GridItem>
                  : null
                }
              </GridContainer>
              <InfoBox labelToken='messages.profile_notification' sx={{ my: 1 }} />
            </CardContent>
            <CardActions>
              <FormButton variant="contained" onClick={handlePasswordChange} startIcon={<VpnKeyIcon />}>
                {t("buttons.password_change")}
              </FormButton>
            </CardActions>
          </Card>
        </Container>
      </Box>
    </AppPage>
  );
}

export default ProfilePage;
